import type { AttendanceReport, ReportDayCell } from "@/lib/reports/build-report";
import { formatReportDateHeader } from "@/lib/reports/build-report";
import type { CellStatus } from "@/lib/types";
import { forwardRef, type CSSProperties } from "react";

/** Excel-style report colors matching coordinator sheets */
const STATUS_BG: Record<CellStatus, string> = {
  P: "#00B050",
  A: "#FF0000",
  O: "#7030A0",
  S: "#ED7D31",
  TP: "#00B0F0",
  L: "#5C1A1A",
  MS: "#5B21B6",
  FS: "#C2410C",
  H: "#7030A0",
  pending: "#F2F2F2",
};

const STATUS_FG: Record<CellStatus, string> = {
  P: "#FFFFFF",
  A: "#FFFFFF",
  O: "#FFFFFF",
  S: "#FFFFFF",
  TP: "#FFFFFF",
  L: "#FFFFFF",
  MS: "#FFFFFF",
  FS: "#FFFFFF",
  H: "#FFFFFF",
  pending: "#888888",
};

/**
 * 100% → green, 0% → red, in-between → yellow shades
 * (higher % = brighter / greener yellow)
 */
function presenceStyle(row: {
  presence_pct: number | null;
  all_out: boolean;
}): { background: string; color: string } {
  if (row.all_out) {
    return { background: "#7030A0", color: "#FFFFFF" };
  }
  if (row.presence_pct == null) {
    return { background: "#F2F2F2", color: "#666666" };
  }
  if (row.presence_pct >= 100) {
    return { background: "#00B050", color: "#FFFFFF" };
  }
  if (row.presence_pct <= 0) {
    return { background: "#FF0000", color: "#FFFFFF" };
  }
  // 0 < pct < 100 → yellow spectrum (darker/amber at low, bright at high)
  if (row.presence_pct >= 75) {
    return { background: "#FFE066", color: "#1a1a1a" };
  }
  if (row.presence_pct >= 50) {
    return { background: "#FFD24D", color: "#1a1a1a" };
  }
  if (row.presence_pct >= 25) {
    return { background: "#FFBF00", color: "#1a1a1a" };
  }
  return { background: "#E6A800", color: "#1a1a1a" };
}

/** Shared cell layout — balanced padding so html2canvas keeps text centered */
const cellBase: CSSProperties = {
  border: "1px solid #000",
  textAlign: "center",
  verticalAlign: "middle",
  padding: "8px 8px",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "16px",
  height: 32,
  boxSizing: "border-box",
};

function StatusCell({ cell }: { cell: ReportDayCell | null }) {
  if (!cell || !cell.label) {
    return (
      <td
        style={{
          ...cellBase,
          background: "#FFFFFF",
        }}
      >
        {"\u00A0"}
      </td>
    );
  }

  return (
    <td
      style={{
        ...cellBase,
        background: STATUS_BG[cell.status],
        color: STATUS_FG[cell.status],
      }}
    >
      {cell.label}
    </td>
  );
}

interface AttendanceReportTableProps {
  report: AttendanceReport;
}

export const AttendanceReportTable = forwardRef<
  HTMLDivElement,
  AttendanceReportTableProps
>(function AttendanceReportTable({ report }, ref) {
  return (
    <div
      ref={ref}
      style={{
        background: "#FFFFFF",
        padding: 16,
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        width: "max-content",
        maxWidth: "100%",
        // Helps html2canvas measure text metrics correctly
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, lineHeight: "22px" }}>
          {report.title}
        </div>
        <div style={{ fontSize: 12, color: "#444", lineHeight: "18px" }}>
          {report.subtitle}
        </div>
        {report.showPresence && (
          <div
            style={{
              fontSize: 10,
              color: "#666",
              marginTop: 4,
              lineHeight: "14px",
            }}
          >
            Presence % = P ÷ (P+A+L) — MS/FS still count as late or absent; O, S, TP excluded
          </div>
        )}
      </div>

      <table
        style={{
          // separate + 0 spacing avoids html2canvas vertical-align bugs with collapse
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: 12,
          tableLayout: "auto",
          lineHeight: "16px",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                ...cellBase,
                background: "#F4C27A",
                whiteSpace: "nowrap",
              }}
            >
              Sr.No
            </th>
            <th
              style={{
                ...cellBase,
                background: "#F4C27A",
                textAlign: "left",
                paddingLeft: 10,
                paddingRight: 10,
                whiteSpace: "nowrap",
              }}
            >
              Devotee
            </th>
            {report.showPresence && (
              <th
                style={{
                  ...cellBase,
                  background: "#F4C27A",
                  whiteSpace: "nowrap",
                }}
              >
                {report.presenceHeader}
              </th>
            )}
            {report.dates.map((d) => (
              <th
                key={d}
                style={{
                  ...cellBase,
                  background: "#F4C27A",
                  whiteSpace: "nowrap",
                }}
              >
                {formatReportDateHeader(d)}
              </th>
            ))}
            {report.showStatusCounts &&
              (["O", "S", "TP", "MS", "FS"] as const).map((code) => (
                <th
                  key={code}
                  style={{
                    ...cellBase,
                    background: "#F4C27A",
                    whiteSpace: "nowrap",
                  }}
                >
                  {code}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row, i) => {
            const pctStyle = presenceStyle(row);
            return (
              <tr key={`${row.person_id}-${row.category ?? i}`}>
                <td style={{ ...cellBase }}>{i + 1}</td>
                <td
                  style={{
                    ...cellBase,
                    background: "#FFFF00",
                    textAlign: "left",
                    paddingLeft: 10,
                    paddingRight: 10,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.person_name}
                </td>
                {report.showPresence && (
                  <td
                    style={{
                      ...cellBase,
                      background: pctStyle.background,
                      color: pctStyle.color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.presence_label}
                  </td>
                )}
                {report.dates.map((d) => (
                  <StatusCell key={d} cell={row.cells[d] ?? null} />
                ))}
                {report.showStatusCounts && (
                  <>
                    <td style={cellBase}>{row.counts?.on_leave ?? 0}</td>
                    <td style={cellBase}>{row.counts?.sick ?? 0}</td>
                    <td style={cellBase}>{row.counts?.taken_permission ?? 0}</td>
                    <td style={cellBase}>{row.counts?.mantra_submitted ?? 0}</td>
                    <td style={cellBase}>{row.counts?.fine_submitted ?? 0}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});
